import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  filterNotifications,
  markAllRead,
  markRead,
  readStore,
  resolveNotificationText,
  seedDemoIfEmpty,
  unreadCount,
} from '../services/appNotifications';
import { formatRelativeTime } from '../utils/formatRelativeTime';

const FILTERS = ['all', 'unread', 'comms', 'tasks', 'leaves', 'docs', 'expenses', 'flow_ia'];

function isSameLocalDay(iso, refDate) {
  let d;
  try {
    d = new Date(iso);
  } catch {
    return false;
  }
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === refDate.getFullYear() &&
    d.getMonth() === refDate.getMonth() &&
    d.getDate() === refDate.getDate()
  );
}

export function NotificationBell() {
  const { t, theme } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [tick, setTick] = useState(0);
  const rootRef = useRef(null);

  const refresh = useCallback(() => setTick((x) => x + 1), []);

  useEffect(() => {
    seedDemoIfEmpty();
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEvt = () => refresh();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('spectra-rrhh-notify-updated', onEvt);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('spectra-rrhh-notify-updated', onEvt);
    };
  }, [refresh]);

  const items = useMemo(() => {
    void tick;
    return readStore().items;
  }, [tick]);

  const nUnread = unreadCount(items);
  const filtered = useMemo(() => filterNotifications(items, filter), [items, filter]);

  const { todayRows, earlierRows } = useMemo(() => {
    const now = new Date();
    const t0 = [];
    const t1 = [];
    filtered.forEach((row) => {
      if (isSameLocalDay(row.createdAt, now)) t0.push(row);
      else t1.push(row);
    });
    return { todayRows: t0, earlierRows: t1 };
  }, [filtered]);

  const typeClass = (type) => {
    const m = {
      comms: 'notify-bell__type--comms',
      surveys: 'notify-bell__type--comms',
      tasks: 'notify-bell__type--tasks',
      training: 'notify-bell__type--tasks',
      leaves: 'notify-bell__type--leaves',
      docs: 'notify-bell__type--docs',
      expenses: 'notify-bell__type--expenses',
      flow_ia: 'notify-bell__type--flow',
    };
    return m[type] || 'notify-bell__type--generic';
  };

  const priClass = (p) => {
    if (p === 'high') return 'notify-bell__pri--high';
    if (p === 'low') return 'notify-bell__pri--low';
    return 'notify-bell__pri--med';
  };

  const commNavPath = (row) => {
    if (!row.link) return null;
    const rid = row.refId != null && row.refId !== '' ? String(row.refId) : '';
    if (row.link === '/communications' && rid) {
      return `/communications?commId=${encodeURIComponent(rid)}`;
    }
    return row.link;
  };

  const openLinked = (row) => {
    const path = commNavPath(row);
    if (!path) return;
    setOpen(false);
    markRead(row.id);
    refresh();
    navigate(path);
  };

  const renderRow = (row) => {
    const { title, body } = resolveNotificationText(t, row);
    const cta = row.ctaKey ? t(row.ctaKey) : t('notifyBell.ctaOpen');
    const canOpen = Boolean(commNavPath(row));
    return (
      <li
        key={row.id}
        className={`notify-bell__item${row.read ? ' notify-bell__item--read' : ''}${canOpen ? ' notify-bell__item--clickable' : ''}`}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={() => {
          if (!canOpen) return;
          openLinked(row);
        }}
        onKeyDown={(e) => {
          if (!canOpen) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openLinked(row);
          }
        }}
      >
        <div className="notify-bell__item-top">
          <span className={`notify-bell__type-dot ${typeClass(row.type)}`} title={row.type} aria-hidden />
          <span className={`notify-bell__pri ${priClass(row.priority)}`} title={t('notifyBell.priority')}>
            {row.priority === 'high' ? '●' : row.priority === 'low' ? '○' : '◐'}
          </span>
          <span className="notify-bell__time">{formatRelativeTime(row.createdAt, t)}</span>
        </div>
        <p className="notify-bell__item-title">{title}</p>
        {body ? <p className="notify-bell__item-body">{body}</p> : null}
        <div className="notify-bell__item-actions">
          {row.link ? (
            <button
              type="button"
              className="btn btn--sm btn--secondary"
              onClick={(e) => {
                e.stopPropagation();
                openLinked(row);
              }}
            >
              {cta}
            </button>
          ) : null}
          {!row.read ? (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={(e) => {
                e.stopPropagation();
                markRead(row.id);
                refresh();
              }}
            >
              {t('notifyBell.markRead')}
            </button>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <div className="notify-bell" ref={rootRef}>
      <button
        type="button"
        className="notify-bell__trigger icon-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('notifyBell.aria')}
        onClick={() => {
          seedDemoIfEmpty();
          setOpen((v) => !v);
          refresh();
        }}
      >
        <span className="notify-bell__icon" aria-hidden>
          🔔
        </span>
        {nUnread > 0 ? (
          <span className="notify-bell__badge" aria-hidden>
            {nUnread > 99 ? '99+' : nUnread}
          </span>
        ) : null}
      </button>

      {open && (
        <div className={`notify-bell__panel panel panel--elevated${theme === 'dark' ? ' notify-bell__panel--dark' : ''}`} role="dialog" aria-label={t('notifyBell.title')}>
          <div className="notify-bell__head">
            <div>
              <h2 className="notify-bell__title">{t('notifyBell.title')}</h2>
              <p className="panel__muted text-sm">{t('notifyBell.subtitle')}</p>
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={nUnread === 0}
              onClick={() => {
                markAllRead();
                refresh();
              }}
            >
              {t('notifyBell.markAllRead')}
            </button>
          </div>

          <div className="notify-bell__tabs" role="tablist">
            {FILTERS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={`notify-bell__tab${filter === id ? ' is-active' : ''}`}
                onClick={() => setFilter(id)}
              >
                {t(`notifyBell.filter_${id}`)}
              </button>
            ))}
          </div>

          <div className="notify-bell__scroll">
            {filtered.length === 0 ? (
              <div className="notify-bell__empty">
                <span className="notify-bell__empty-icon" aria-hidden>
                  ✦
                </span>
                <p className="notify-bell__empty-title">{t('notifyBell.emptyTitle')}</p>
                <p className="panel__muted text-sm">{t('notifyBell.emptyBody')}</p>
                <Link to="/communications" className="btn btn--secondary btn--sm" onClick={() => setOpen(false)}>
                  {t('notifyBell.emptyCta')}
                </Link>
              </div>
            ) : (
              <>
                {todayRows.length > 0 && (
                  <section className="notify-bell__section">
                    <h3 className="notify-bell__section-title">{t('notifyBell.sectionToday')}</h3>
                    <ul className="notify-bell__list">{todayRows.map(renderRow)}</ul>
                  </section>
                )}
                {earlierRows.length > 0 && (
                  <section className="notify-bell__section">
                    <h3 className="notify-bell__section-title">{t('notifyBell.sectionEarlier')}</h3>
                    <ul className="notify-bell__list">{earlierRows.map(renderRow)}</ul>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
